import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import { breadcrumbParts } from '../orgChartModel';

// Konum satırı (spec 2026-10-24 §5.5): "Konum: Kök Ad › … › Seçili Ad" — kökten seçili karta. Halkada kişi adı (boşsa
// ünvan), ipucunda ünvan. Uzun zincirde ilk halka + "…" (açılır menü) + son 3. Seçim yoksa `emptyLabel` (Tüm Şirket).

const Bar = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  min-width: 0;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
`;

const Link = styled.button`
  border: none;
  background: transparent;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: inherit;
  font-size: 13px;
  font-weight: ${({ $current }) => ($current ? 700 : 600)};
  color: ${({ $current, theme }) => ($current ? theme.colors.primary.main : '#1F2A37')};
  cursor: pointer;
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover { background: rgba(2, 145, 239, 0.08); }
`;

const Label = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  margin-right: 4px;
`;

const Sep = () => <ChevronRight size={14} color="#A0A8B4" aria-hidden="true" />;

const MenuWrap = styled.span`
  position: relative;
  display: inline-flex;
`;

const Menu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 20;
  margin-top: 4px;
  min-width: 220px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 66, 66, 0.15);
  padding: 4px;
  display: flex;
  flex-direction: column;
`;

const MenuItem = styled.button`
  text-align: left;
  border: none;
  background: transparent;
  padding: 6px 10px;
  border-radius: 4px;
  font-family: inherit;
  font-size: 13px;
  color: #004242;
  cursor: pointer;
  &:hover { background: rgba(2, 145, 239, 0.08); }
`;

export default function OrgBreadcrumb({ crumbs, onSelect, emptyLabel }) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const list = crumbs || [];
  const lastId = list.length ? list[list.length - 1].positionId : null;
  const { head, hidden, tail } = breadcrumbParts(list);
  const link = (crumb, index) => (
    <React.Fragment key={crumb.positionId}>
      {index > 0 && <Sep />}
      <Link
        type="button"
        data-testid={`crumb-${crumb.positionId}`}
        title={crumb.title || undefined}
        $current={crumb.positionId === lastId}
        onClick={() => onSelect(crumb.positionId)}
      >
        {crumb.label}
      </Link>
    </React.Fragment>
  );

  return (
    <Bar data-testid="org-breadcrumb" aria-label={t('orgChart.location')}>
      <Label>{t('orgChart.location')}:</Label>
      {list.length === 0 && <span data-testid="crumb-empty">{emptyLabel}</span>}
      {head.map((c, i) => link(c, i))}
      {hidden.length > 0 && (
        <>
          <Sep />
          <MenuWrap ref={menuRef}>
            <Link type="button" data-testid="crumb-more" aria-label={t('orgChart.breadcrumbMore')} onClick={() => setMenuOpen((o) => !o)}>
              <MoreHorizontal size={14} />
            </Link>
            {menuOpen && (
              <Menu role="menu">
                {hidden.map((crumb) => (
                  <MenuItem
                    key={crumb.positionId}
                    type="button"
                    role="menuitem"
                    data-testid={`crumb-${crumb.positionId}`}
                    onClick={() => {
                      setMenuOpen(false);
                      onSelect(crumb.positionId);
                    }}
                  >
                    {crumb.label}
                  </MenuItem>
                ))}
              </Menu>
            )}
          </MenuWrap>
        </>
      )}
      {tail.map((c) => link(c, 1))}
    </Bar>
  );
}
